// client/src/pages/admin/ManageSendingDomains.js
// Admin page for managing email sending domains (multi-domain reputation isolation).
// Allows creating, editing, and retiring domains with warmup phase tracking.

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import adminService from '../../services/adminService.js';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'text-green-400' },
  { value: 'warming', label: 'Warming Up', color: 'text-yellow-400' },
  { value: 'suspended', label: 'Suspended', color: 'text-red-400' },
  { value: 'retired', label: 'Retired', color: 'text-gray-400' },
];

const WARMUP_PHASES = [
  { value: 1, label: 'Phase 1 — New (1-3s delays)', description: 'Conservative sending for brand new domains' },
  { value: 2, label: 'Phase 2 — Building (0.5-1.5s delays)', description: '2-3 weeks of good sending history' },
  { value: 3, label: 'Phase 3 — Established (0.2-0.4s delays)', description: 'Good reputation, faster sending' },
  { value: 4, label: 'Phase 4 — Mature (no delays)', description: 'Fully warmed, maximum speed' },
];

const PROVIDER_OPTIONS = [
  { value: 'resend', label: 'Resend', description: 'Primary provider via AWS SES' },
  { value: 'elastic', label: 'Elastic Email', description: 'Alternative provider' },
  { value: 'ses', label: 'AWS SES (Direct)', description: 'Direct AWS SES integration' },
];

export default function ManageSendingDomains() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    domain: '',
    from_email: '',
    display_name: '',
    status: 'warming',
    warmup_phase: 1,
    daily_limit: '',
    notes: '',
    provider: 'resend',
  });

  useEffect(() => {
    loadDomains();
  }, []);

  async function loadDomains() {
    try {
      setLoading(true);
      const res = await adminService.getSendingDomains();
      setDomains(res.sendingDomains || []);
    } catch (err) {
      setError('Failed to load sending domains');
    } finally {
      setLoading(false);
    }
  }

  function openCreateModal() {
    setEditingDomain(null);
    setForm({
      domain: '',
      from_email: '',
      display_name: '',
      status: 'warming',
      warmup_phase: 1,
      daily_limit: '',
      notes: '',
      provider: 'resend',
    });
    setShowModal(true);
    setError('');
  }

  function openEditModal(domain) {
    setEditingDomain(domain);
    setForm({
      domain: domain.domain || '',
      from_email: domain.from_email || '',
      display_name: domain.display_name || '',
      status: domain.status || 'active',
      warmup_phase: domain.warmup_phase || 1,
      daily_limit: domain.daily_limit ?? '',
      notes: domain.notes || '',
      provider: domain.provider || 'resend',
    });
    setShowModal(true);
    setError('');
  }

  function closeModal() {
    setShowModal(false);
    setEditingDomain(null);
    setError('');
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        ...form,
        daily_limit: form.daily_limit === '' ? null : parseInt(form.daily_limit),
        warmup_phase: parseInt(form.warmup_phase),
      };

      if (editingDomain) {
        await adminService.updateSendingDomain(editingDomain.domainid, payload);
        setSuccess('Domain updated successfully');
      } else {
        await adminService.createSendingDomain(payload);
        setSuccess('Domain created successfully');
      }

      closeModal();
      loadDomains();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save domain');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(domain) {
    if (!window.confirm(`Delete "${domain.domain}"? This cannot be undone.`)) return;

    try {
      await adminService.deleteSendingDomain(domain.domainid);
      setSuccess('Domain deleted');
      loadDomains();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete domain');
      setTimeout(() => setError(''), 5000);
    }
  }

  async function handleResetDailyCount(domain) {
    try {
      await adminService.resetSendingDomainDailyCount(domain.domainid);
      setSuccess(`Daily count reset for ${domain.domain}`);
      loadDomains();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to reset daily count');
      setTimeout(() => setError(''), 5000);
    }
  }

  function getStatusBadge(status) {
    const opt = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${opt.color} bg-royal-700`}>
        {opt.label}
      </span>
    );
  }

  function getProviderBadge(provider) {
    const colors = {
      resend: 'text-blue-400 bg-blue-900/30',
      elastic: 'text-purple-400 bg-purple-900/30',
      ses: 'text-orange-400 bg-orange-900/30',
    };
    const labels = {
      resend: 'Resend',
      elastic: 'Elastic',
      ses: 'SES',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[provider] || colors.resend}`}>
        {labels[provider] || provider}
      </span>
    );
  }

  function getWarmupLabel(phase) {
    const opt = WARMUP_PHASES.find(o => o.value === phase);
    return opt ? `Phase ${phase}` : `Phase ${phase}`;
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <p className="text-royal-400">Loading sending domains...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/admin" className="text-royal-400 hover:text-gold text-sm mb-2 inline-block">
            ← Back to Admin
          </Link>
          <h1 className="font-display text-3xl text-gold">Sending Domains</h1>
          <p className="text-royal-400 mt-1">
            Manage email sending domains for reputation isolation. Assign domains to user tiers or marketing campaigns.
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-gold text-royal-900 px-4 py-2 rounded-lg font-semibold hover:bg-gold/90 transition-colors"
        >
          + Add Domain
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-500/20 border border-green-500 text-green-200 px-4 py-3 rounded-lg mb-4">
          {success}
        </div>
      )}

      {/* Domains Table */}
      {domains.length === 0 ? (
        <div className="bg-royal-800/40 border border-royal-600 rounded-xl p-8 text-center">
          <p className="text-royal-400 mb-4">No sending domains configured yet.</p>
          <p className="text-royal-500 text-sm mb-4">
            Add your first domain to start sending emails with reputation isolation.
          </p>
          <button
            onClick={openCreateModal}
            className="bg-gold text-royal-900 px-4 py-2 rounded-lg font-semibold hover:bg-gold/90"
          >
            + Add Your First Domain
          </button>
        </div>
      ) : (
        <div className="bg-royal-800/40 border border-royal-600 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-royal-600 text-left">
                <th className="px-4 py-3 text-royal-400 font-medium">Domain</th>
                <th className="px-4 py-3 text-royal-400 font-medium">From Email</th>
                <th className="px-4 py-3 text-royal-400 font-medium">Provider</th>
                <th className="px-4 py-3 text-royal-400 font-medium">Status</th>
                <th className="px-4 py-3 text-royal-400 font-medium">Warmup</th>
                <th className="px-4 py-3 text-royal-400 font-medium">Today's Sends</th>
                <th className="px-4 py-3 text-royal-400 font-medium">Usage</th>
                <th className="px-4 py-3 text-royal-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((domain) => (
                <tr key={domain.domainid} className="border-b border-royal-700 hover:bg-royal-700/30">
                  <td className="px-4 py-3">
                    <div className="text-white font-medium">{domain.domain}</div>
                    {domain.display_name && (
                      <div className="text-royal-400 text-sm">{domain.display_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-royal-300 text-sm">{domain.from_email}</td>
                  <td className="px-4 py-3">{getProviderBadge(domain.provider)}</td>
                  <td className="px-4 py-3">{getStatusBadge(domain.status)}</td>
                  <td className="px-4 py-3">
                    <span className="text-royal-300">{getWarmupLabel(domain.warmup_phase)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-white">
                        {domain.emails_sent_today || 0}
                        {domain.daily_limit && (
                          <span className="text-royal-400"> / {domain.daily_limit}</span>
                        )}
                      </span>
                      {(domain.emails_sent_today > 0) && (
                        <button
                          onClick={() => handleResetDailyCount(domain)}
                          className="text-royal-400 hover:text-gold text-xs"
                          title="Reset daily count"
                        >
                          ↺
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {domain.tier_count > 0 && (
                      <div className="text-blue-400">{domain.tier_count} tier(s)</div>
                    )}
                    {domain.campaign_count > 0 && (
                      <div className="text-purple-400">{domain.campaign_count} campaign(s)</div>
                    )}
                    {!domain.tier_count && !domain.campaign_count && (
                      <span className="text-royal-500">Not assigned</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEditModal(domain)}
                        className="text-gold hover:text-gold/80 text-sm"
                      >
                        Edit
                      </button>
                      {!domain.tier_count && !domain.campaign_count && (
                        <button
                          onClick={() => handleDelete(domain)}
                          className="text-red-400 hover:text-red-300 text-sm"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 bg-royal-800/30 border border-royal-600 rounded-xl p-4">
        <h3 className="text-gold font-semibold mb-2">How Sending Domains Work</h3>
        <ul className="text-royal-400 text-sm space-y-1">
          <li>• <strong>Provider</strong> determines which email service sends the email (Resend, Elastic Email, or direct SES)</li>
          <li>• <strong>Warmup Phases</strong> control send delays to protect domain reputation with new domains</li>
          <li>• <strong>Daily Limits</strong> prevent over-sending during warmup (leave blank for unlimited)</li>
          <li>• <strong>Assign to Tiers</strong> in Plans & Tiers to route eCard emails through specific domains</li>
          <li>• <strong>Assign to Campaigns</strong> in Marketing Campaigns for promotional email isolation</li>
          <li>• <strong>Retire</strong> a domain if it gets spam complaints, then create a new one</li>
        </ul>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-royal-800 border border-royal-600 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="font-display text-2xl text-gold mb-4">
                {editingDomain ? 'Edit Sending Domain' : 'Add Sending Domain'}
              </h2>

              {error && (
                <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-4">
                  {error}
                </div>
              )}

              <form onSubmit={handleSave} className="space-y-4">
                {/* Domain */}
                <div>
                  <label className="block text-royal-300 text-sm mb-1">
                    Domain <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.domain}
                    onChange={(e) => setForm({ ...form, domain: e.target.value })}
                    placeholder="send.example.com"
                    className="w-full bg-royal-900 border border-royal-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                    required
                  />
                  <p className="text-royal-500 text-xs mt-1">
                    Must be verified with your email provider (DKIM, SPF, DMARC configured)
                  </p>
                </div>

                {/* From Email */}
                <div>
                  <label className="block text-royal-300 text-sm mb-1">
                    From Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    value={form.from_email}
                    onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                    placeholder="hello@send.example.com"
                    className="w-full bg-royal-900 border border-royal-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                    required
                  />
                </div>

                {/* Display Name */}
                <div>
                  <label className="block text-royal-300 text-sm mb-1">Display Name</label>
                  <input
                    type="text"
                    value={form.display_name}
                    onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                    placeholder="The Cherry Fairy Shop"
                    className="w-full bg-royal-900 border border-royal-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                  />
                  <p className="text-royal-500 text-xs mt-1">
                    Shows as "Display Name &lt;email@domain.com&gt;" in recipient's inbox
                  </p>
                </div>

                {/* Provider */}
                <div>
                  <label className="block text-royal-300 text-sm mb-1">Email Provider</label>
                  <select
                    value={form.provider}
                    onChange={(e) => setForm({ ...form, provider: e.target.value })}
                    className="w-full bg-royal-900 border border-royal-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                  >
                    {PROVIDER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-royal-500 text-xs mt-1">
                    {PROVIDER_OPTIONS.find(p => p.value === form.provider)?.description}
                  </p>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-royal-300 text-sm mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full bg-royal-900 border border-royal-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Warmup Phase */}
                <div>
                  <label className="block text-royal-300 text-sm mb-1">Warmup Phase</label>
                  <select
                    value={form.warmup_phase}
                    onChange={(e) => setForm({ ...form, warmup_phase: e.target.value })}
                    className="w-full bg-royal-900 border border-royal-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                  >
                    {WARMUP_PHASES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-royal-500 text-xs mt-1">
                    {WARMUP_PHASES.find(p => p.value === parseInt(form.warmup_phase))?.description}
                  </p>
                </div>

                {/* Daily Limit */}
                <div>
                  <label className="block text-royal-300 text-sm mb-1">Daily Limit</label>
                  <input
                    type="number"
                    value={form.daily_limit}
                    onChange={(e) => setForm({ ...form, daily_limit: e.target.value })}
                    placeholder="Leave blank for unlimited"
                    min="0"
                    className="w-full bg-royal-900 border border-royal-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                  />
                  <p className="text-royal-500 text-xs mt-1">
                    Maximum emails per day. Useful during warmup to prevent over-sending.
                  </p>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-royal-300 text-sm mb-1">Notes</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Internal notes about this domain..."
                    rows={2}
                    className="w-full bg-royal-900 border border-royal-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none resize-none"
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-royal-600">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-royal-300 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-gold text-royal-900 px-4 py-2 rounded-lg font-semibold hover:bg-gold/90 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : editingDomain ? 'Update Domain' : 'Create Domain'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}